import { useState } from "react";
import { useNavigate } from "react-router";
import { useAppContext } from "@/providers";
import { completeProfile } from "@/lib/api/user";
import { t } from "@/lib/i18n/messages";
import { Button } from "@/components/ui/button";

export default function CompleteProfilePage() {
  const { locale } = useAppContext();
  const isRTL = locale === "ar";
  const navigate = useNavigate();

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    gender: "" as "" | "male" | "female",
    date_of_birth: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});
    try {
      const res = await completeProfile({
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        gender: form.gender || null,
        date_of_birth: form.date_of_birth || null,
        locale,
      });
      if (res.success) {
        navigate("/dashboard");
      } else {
        const fieldErrors: Record<string, string> = {};
        if (res.errors) {
          Object.entries(res.errors).forEach(([k, msgs]) => {
            fieldErrors[k] = msgs[0] ?? "";
          });
        } else {
          fieldErrors["form"] = res.message ?? t(locale, "error");
        }
        setErrors(fieldErrors);
      }
    } catch {
      setErrors({ form: t(locale, "error") });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full h-10 px-3 rounded-[var(--radius-sm)] border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm transition-colors duration-base";
  const labelClass = "block text-sm font-medium text-foreground mb-1.5";

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="min-h-screen bg-background flex items-center justify-center p-4"
    >
      <div className="w-full max-w-md bg-card rounded-lg shadow-lg border border-border p-8 relative overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-primary via-secondary to-brand-green" />
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
            <span className="material-icons-outlined text-primary text-2xl">
              manage_accounts
            </span>
          </div>
        </div>
        <h1 className="text-xl font-bold text-foreground mb-1 text-center">
          {locale === "ar" ? "أكمل ملفك الشخصي" : "Complete Your Profile"}
        </h1>
        <p className="text-foreground-secondary text-sm mb-8 text-center">
          {locale === "ar"
            ? "أضف بعض المعلومات لتحسين تجربتك"
            : "Add some info to improve your experience"}
        </p>

        {errors["form"] && (
          <p className="text-xs text-error mb-4 text-center" role="alert">
            {errors["form"]}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>
                {locale === "ar" ? "الاسم الأول" : "First Name"}
              </label>
              <input
                className={inputClass}
                value={form.first_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, first_name: e.target.value }))
                }
              />
              {errors["first_name"] && (
                <p className="text-xs text-error mt-1">{errors["first_name"]}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                {locale === "ar" ? "اسم العائلة" : "Last Name"}
              </label>
              <input
                className={inputClass}
                value={form.last_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, last_name: e.target.value }))
                }
              />
              {errors["last_name"] && (
                <p className="text-xs text-error mt-1">{errors["last_name"]}</p>
              )}
            </div>
          </div>
          <div>
            <label className={labelClass}>
              {locale === "ar" ? "الجنس" : "Gender"}
            </label>
            <div className="relative">
              <select
                className={`${inputClass} appearance-none bg-none pe-10`}
                value={form.gender}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    gender: e.target.value as "" | "male" | "female",
                  }))
                }
              >
              <option value="">
                {locale === "ar" ? "-- اختر --" : "-- Select --"}
              </option>
              <option value="male">
                {locale === "ar" ? "ذكر" : "Male"}
              </option>
              <option value="female">
                {locale === "ar" ? "أنثى" : "Female"}
              </option>
            </select>
            <span className="material-icons-outlined absolute top-1/2 -translate-y-1/2 end-3 pointer-events-none text-gray-400">expand_more</span>
            {errors["gender"] && (
              <p className="text-xs text-error mt-1">{errors["gender"]}</p>
            )}
            </div>
          </div>
          <div>
            <label className={labelClass}>
              {locale === "ar" ? "تاريخ الميلاد" : "Date of Birth"}
            </label>
            <input
              type="date"
              className={inputClass}
              value={form.date_of_birth}
              onChange={(e) =>
                setForm((f) => ({ ...f, date_of_birth: e.target.value }))
              }
            />
            {errors["date_of_birth"] && (
              <p className="text-xs text-error mt-1">
                {errors["date_of_birth"]}
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => navigate("/dashboard")}
            >
              {locale === "ar" ? "تخطي" : "Skip"}
            </Button>
            <Button
              type="submit"
              className="flex-1"
              isLoading={submitting}
            >
              {locale === "ar" ? "حفظ" : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
